import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled, { keyframes, css } from 'styled-components';
import { FiX, FiClock, FiAlertCircle } from 'react-icons/fi';
import { doClaimCase } from '../../actions/aegonQueue';
import { setActiveConsult } from '../../actions';
import {
  colors,
  space,
  radius,
  fontSize,
  fontWeight,
  lineHeight,
  fontFamily,
  shadow,
  focusRing,
  motion,
  z,
  withAlpha,
} from '../../styles/tokens';

// Pin dark-surface tokens for shorter property paths inside template strings.
const t = {
  bg:           colors.dark.bg,
  surface:      colors.dark.surface,
  surfaceHi:    colors.dark.surfaceHi,
  border:       colors.dark.border,
  borderStrong: colors.dark.borderStrong,
  text:         colors.dark.text,
  textMuted:    colors.dark.textMuted,
  textDim:      colors.dark.textDim,
  primary:      colors.primary,
  primaryHi:    colors.primaryHi,
  success:      colors.success,
  successHi:    colors.successHi,
  warning:      colors.warning,
  warningHi:    colors.warningHi,
  danger:       colors.danger,
  dangerHi:     colors.dangerHi,
};

// ────────────────────────────────────────────────────────────────────
// Animations
// ────────────────────────────────────────────────────────────────────

const overlayFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(-8px) scale(0.985); }
  to   { opacity: 1; transform: none; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; }
  40%           { opacity: 1; }
`;

// ────────────────────────────────────────────────────────────────────
// Reusable fragments
// ────────────────────────────────────────────────────────────────────

const focusRingMixin = (color = t.primary) => css`
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing(color, 0.4)};
  }
`;

const tabularNums = css`
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

// ────────────────────────────────────────────────────────────────────
// Layout chrome
// ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${z.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space[4]};
  background: rgba(7, 9, 14, 0.55);
  -webkit-backdrop-filter: blur(8px) saturate(140%);
  backdrop-filter: blur(8px) saturate(140%);
  animation: ${overlayFade} ${motion.base};
`;

const Card = styled.div`
  position: relative;
  width: 46rem;
  max-width: 100%;
  padding: ${space[6]} ${space[6]} ${space[7]};
  background: ${t.bg};
  border: 1px solid ${t.border};
  border-radius: ${radius.xl};
  box-shadow: ${shadow.modal};
  color: ${t.text};
  font-family: ${fontFamily.sans};
  font-size: ${fontSize.base};
  line-height: ${lineHeight.normal};
  animation: ${cardIn} ${motion.slow};

  /* 1px reflected light hairline — same recipe as AegonInstantPanel. */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: ${space[5]}; right: ${space[5]};
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${withAlpha(t.text, 0.08)} 50%,
      transparent
    );
    pointer-events: none;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space[4]};
  margin-bottom: ${space[4]};
`;

const Eyebrow = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${t.primary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const DismissBtn = styled.button`
  flex-shrink: 0;
  width: 3.2rem;
  height: 3.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${t.border};
  border-radius: ${radius.md};
  color: ${t.textMuted};
  cursor: pointer;
  transition: color ${motion.fast}, border-color ${motion.fast}, background ${motion.fast};

  &:hover {
    color: ${t.text};
    background: ${t.surface};
    border-color: ${t.borderStrong};
  }
  ${focusRingMixin(t.primary)}
`;

// ────────────────────────────────────────────────────────────────────
// Customer block
// ────────────────────────────────────────────────────────────────────

const CustomerName = styled.h2`
  margin: 0 0 ${space[1]};
  font-size: ${fontSize['2xl']};
  font-weight: ${fontWeight.semibold};
  letter-spacing: -0.015em;
  line-height: ${lineHeight.tight};
  color: ${t.text};
`;

const WaitBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  margin-top: ${space[2]};
  padding: ${space[1]} ${space[2]};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${t.warning};
  background: ${withAlpha(t.warning, 0.12)};
  border: 1px solid ${withAlpha(t.warning, 0.25)};
  border-radius: ${radius.sm};
  ${tabularNums}
`;

// ────────────────────────────────────────────────────────────────────
// Meta grid
// ────────────────────────────────────────────────────────────────────

const MetaGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${space[3]} ${space[5]};
  margin: ${space[4]} 0 0;
  padding: ${space[3]} ${space[4]};
  background: ${t.surface};
  border: 1px solid ${t.border};
  border-radius: ${radius.md};
`;

const MetaCell = styled.div`
  min-width: 0;
`;

const MetaLabel = styled.dt`
  margin: 0 0 ${space[1]};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.medium};
  color: ${t.textDim};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const MetaValue = styled.dd`
  margin: 0;
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${t.text};
  word-break: break-word;
  ${tabularNums}
`;

// ────────────────────────────────────────────────────────────────────
// Primary CTA
// ────────────────────────────────────────────────────────────────────

const StartBtn = styled.button`
  width: 100%;
  margin-top: ${space[5]};
  padding: ${space[3]} ${space[4]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space[2]};
  background: linear-gradient(180deg, ${t.successHi}, ${t.success});
  color: #04140a;
  border: 1px solid ${withAlpha('#000', 0.15)};
  border-radius: ${radius.md};
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow:
    0 1px 0 ${withAlpha('#fff', 0.18)} inset,
    ${shadow.sm};
  transition: transform ${motion.fast}, box-shadow ${motion.fast}, filter ${motion.fast};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 1px 0 ${withAlpha('#fff', 0.22)} inset,
      0 6px 16px -6px ${withAlpha(t.success, 0.55)};
  }
  &:active:not(:disabled) { transform: translateY(0); filter: brightness(0.96); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  ${focusRingMixin(t.success)}
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 0.3rem;

  span {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: currentColor;
    animation: ${dotPulse} 1.2s infinite ease-in-out both;
  }
  span:nth-child(2) { animation-delay: 0.16s; }
  span:nth-child(3) { animation-delay: 0.32s; }
`;

// ────────────────────────────────────────────────────────────────────
// Status / error message
// ────────────────────────────────────────────────────────────────────

const StatusMsg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space[1]};
  margin-top: ${space[2]};
  padding: ${space[2]} ${space[3]};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  color: ${(p) => (p.error ? t.dangerHi : t.textMuted)};
  background: ${(p) => (p.error ? withAlpha(t.danger, 0.08) : 'transparent')};
  border: 1px solid ${(p) => (p.error ? withAlpha(t.danger, 0.25) : 'transparent')};
  border-radius: ${radius.sm};
`;

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

const fmtSeconds = (s) => {
  if (!s && s !== 0) return null;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────

function AegonCaseNotification({ caseData, onDismiss, dispatch }) {
  const [claiming, setClaiming] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [isError, setIsError] = useState(false);

  // ESC dismisses — standard expectation for any modal-style overlay.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const handleStartCall = async () => {
    if (claiming) return;
    setClaiming(true);
    setStatusMsg(null);

    const result = await dispatch(doClaimCase(caseData.consultationId));

    if (!result) {
      setStatusMsg('Something went wrong. Please try again.');
      setIsError(true);
      setClaiming(false);
      return;
    }

    if (result.status === 'already_taken') {
      setStatusMsg('This case was already taken by another doctor.');
      setIsError(true);
      setClaiming(false);
      return;
    }

    if (result.status === 'success') {
      dispatch(setActiveConsult({
        consultationId: caseData.consultationId,
        callId: result.callId,
        user: {
          userId: caseData.userId || result.userId || null,
          userName: caseData.customerName,
          gender: caseData.gender || '',
        },
        labels: [],
        oneConnectLinks: [],
        relatives: [],
        requestType: 'video',
        callType: 'video',
        channel: `aegon-instant-${caseData.consultationId}`,
      }));
      dispatch({
        type: 'INITIATE_VIDEO_CALL',
        payload: {
          isInsta: 1,
          instantConsultationId: caseData.consultationId,
        },
      });
      onDismiss();
    }
  };

  const waitStr = fmtSeconds(caseData.waitSeconds);

  return (
    <Overlay
      onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
      role="presentation"
    >
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby="aegon-case-notification-title"
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Eyebrow>Incoming Case</Eyebrow>
          <DismissBtn type="button" onClick={onDismiss} aria-label="Dismiss">
            <FiX size={16} aria-hidden="true" />
          </DismissBtn>
        </Header>

        <CustomerName id="aegon-case-notification-title">
          {caseData.customerName}
        </CustomerName>
        {waitStr && (
          <WaitBadge>
            <FiClock size={12} aria-hidden="true" />
            Waited {waitStr}
          </WaitBadge>
        )}

        <MetaGrid>
          <MetaCell>
            <MetaLabel>Proposal Number</MetaLabel>
            <MetaValue>{caseData.proposalNumber || '—'}</MetaValue>
          </MetaCell>
          <MetaCell>
            <MetaLabel>Gender</MetaLabel>
            <MetaValue>{caseData.gender || '—'}</MetaValue>
          </MetaCell>
          <MetaCell>
            <MetaLabel>Phone</MetaLabel>
            <MetaValue>{caseData.phone || '—'}</MetaValue>
          </MetaCell>
        </MetaGrid>

        <StartBtn
          type="button"
          onClick={handleStartCall}
          disabled={claiming}
          aria-busy={claiming}
        >
          {claiming ? (
            <>
              Connecting
              <Dots aria-hidden="true"><span /><span /><span /></Dots>
            </>
          ) : 'Start Call Now'}
        </StartBtn>

        {statusMsg && (
          <StatusMsg error={isError} role={isError ? 'alert' : 'status'}>
            {isError && <FiAlertCircle size={14} aria-hidden="true" />}
            {statusMsg}
          </StatusMsg>
        )}
      </Card>
    </Overlay>
  );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(AegonCaseNotification);
